import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { GraduationCap, ChevronLeft, Calendar, Clock, ExternalLink } from "lucide-react";

const fmtUsd = (c) => `$${(c / 100).toFixed(c % 100 === 0 ? 0 : 2)}`;

const STATUS_STYLE = {
  pending:   { bg: "rgba(245,158,11,0.15)",  fg: "#FCD34D", label: "Awaiting payment" },
  confirmed: { bg: "rgba(16,185,129,0.15)",  fg: "#6EE7B7", label: "Confirmed" },
  cancelled: { bg: "rgba(220,38,38,0.18)",   fg: "#FCA5A5", label: "Cancelled" },
};

export default function PlayerBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/lessons/bookings/me").then(({ data }) => setBookings(data.bookings || [])).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen pb-16" style={{ backgroundColor: "var(--ink-1)" }}>
      <header className="sticky top-0 z-30 backdrop-blur-xl border-b border-[var(--ink-border)]" style={{ backgroundColor: "rgba(10,20,36,0.85)" }}>
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Logo size="sm" variant="lockup" linkTo="/portal" dark />
          <Link to="/portal" className="text-sm text-white/60 hover:text-white inline-flex items-center gap-1" data-testid="back-to-portal">
            <ChevronLeft className="h-4 w-4" /> Back to portal
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-5 mt-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: "var(--brand-secondary)" }}>My Lessons</div>
            <h1 className="font-display font-black text-3xl text-white tracking-tight mt-1 flex items-center gap-2">
              <GraduationCap className="h-7 w-7" /> Booked sessions
            </h1>
          </div>
          <Link to="/lessons">
            <Button className="text-white font-bold red-glow" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="book-new">
              Book another
            </Button>
          </Link>
        </div>

        <div className="mt-6 space-y-3">
          {loading && <div className="text-white/40 text-sm">Loading…</div>}
          {!loading && bookings.length === 0 && (
            <div className="rounded-xl ink-card p-10 text-center">
              <GraduationCap className="h-10 w-10 mx-auto text-white/30 mb-2" />
              <div className="font-display font-bold text-lg text-white">No bookings yet</div>
              <p className="text-sm text-white/50 mt-1">Browse coaches in the marketplace and lock in your first session.</p>
              <Link to="/lessons"><Button className="mt-4 text-white font-bold" style={{ backgroundColor: "var(--brand-secondary)" }}>Browse coaches</Button></Link>
            </div>
          )}
          {bookings.map((b) => {
            const s = STATUS_STYLE[b.status] || STATUS_STYLE.pending;
            return (
              <div key={b.booking_id} className="rounded-xl ink-card p-4" data-testid={`booking-${b.booking_id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <div className="font-display font-bold text-white text-base">{b.coach_name}</div>
                    <div className="text-xs text-white/55">{b.lesson_label} · {b.duration_min} min</div>
                  </div>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide"
                    style={{ backgroundColor: s.bg, color: s.fg, border: `1px solid ${s.fg}55` }}>
                    {s.label}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-1.5 text-white/70"><Calendar className="h-3.5 w-3.5" />
                    <span className="font-mono-num">{new Date(b.slot_iso).toLocaleString()}</span></div>
                  <div className="text-right text-white font-bold font-mono-num">{fmtUsd(b.amount_cents)}</div>
                </div>
                {b.student_notes && <div className="text-xs text-white/50 mt-2 italic">&ldquo;{b.student_notes}&rdquo;</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
